/**
 * Login social prin popup (widget embedded), ca functii pure: fara DOM, fara importuri, fara retea.
 * controller.js le leaga de window / api; aici stau doar deciziile, testabile fara browser.
 *
 * Contractul cu serverul (claim OAuth):
 *  1. Popup-ul pleaca cu ?bw_origin=<originea paginii care gazduieste widgetul>; serverul o normalizeaza
 *     si o tine langa tokenul de sesiune al fluxului.
 *  2. Callback-ul NU mai logheaza sesiunea tokenului ales de client. Trimite catre window.opener, DOAR pe
 *     originea retinuta, {type:'oauth-success', user, claim} - sau {type:'oauth-error'} fara date.
 *  3. Widgetul schimba claim-ul (unic, 120 s, legat de tokenul LUI) pe POST routes.oauthClaim; abia raspunsul
 *     200 {user} inseamna login. Serverele vechi trimit oauth-success fara claim: comportamentul de azi.
 */

const TRUSTED_ORIGIN_SUFFIXES = ['.zenagenda.com', '.ontimeagenda.com'];

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

/** Regula existenta: mesajul trebuie sa vina de pe un subdomeniu al nostru (popup-ul e pe book.*). */
export function isTrustedPopupOrigin(origin) {
    return typeof origin === 'string' && TRUSTED_ORIGIN_SUFFIXES.some((suffix) => origin.endsWith(suffix));
}

/**
 * Originea paginii gazda ('scheme://host[:port]'), cum o vede browserul. Serverul o normalizeaza oricum
 * (EmbedOrigin::normalize) si respinge ce nu e http/https; aici doar o citim, cu rezerva pentru
 * browserele fara location.origin. '' cand nu se poate afla - parametrul nu se mai adauga.
 */
export function embedOrigin(loc) {
    if (!loc) return '';
    if (typeof loc.origin === 'string' && loc.origin !== '') return loc.origin;
    if (typeof loc.protocol === 'string' && typeof loc.host === 'string' && loc.protocol && loc.host) {
        return loc.protocol + '//' + loc.host;
    }
    return '';
}

/** Adauga un parametru de query pastrand query-ul existent (?embedded=true&session_token=...) si fragmentul. */
export function withQueryParam(url, key, value) {
    if (typeof url !== 'string' || url === '' || value === undefined || value === null || value === '') {
        return url;
    }
    const hashAt = url.indexOf('#');
    const base = hashAt === -1 ? url : url.slice(0, hashAt);
    const hash = hashAt === -1 ? '' : url.slice(hashAt);
    return base + (base.includes('?') ? '&' : '?')
        + encodeURIComponent(key) + '=' + encodeURIComponent(String(value)) + hash;
}

/**
 * Ce facem cu un mesaj venit de pe o origine de incredere:
 *  - {kind:'claim', claim, user}  oauth-success de la un server nou: login DOAR dupa schimbul claim-ului;
 *  - {kind:'legacy', user}        oauth-success fara cheia claim (server vechi): ca pana acum;
 *  - {kind:'error'}               oauth-error, sau un claim prezent dar invalid (gol / nu e sir);
 *  - {kind:'ignore'}              orice alt mesaj (alte scripturi / iframe-uri de pe domeniile noastre):
 *                                 ascultatorul ramane, fluxul popup-ului inca n-a raspuns.
 */
export function classifyPopupMessage(data) {
    if (!isObj(data)) return {kind: 'ignore'};
    if (data.type === 'oauth-error') return {kind: 'error'};
    if (data.type !== 'oauth-success') return {kind: 'ignore'};
    const user = isObj(data.user) ? data.user : null;
    if (Object.prototype.hasOwnProperty.call(data, 'claim')) {
        return (typeof data.claim === 'string' && data.claim !== '')
            ? {kind: 'claim', claim: data.claim, user}
            : {kind: 'error'};
    }
    return {kind: 'legacy', user};
}

/** Utilizatorul din raspunsul 200 al claim-ului ({user: publicDataOnly()}); null = raspuns neasteptat. */
export function claimedUser(res) {
    return (isObj(res) && isObj(res.user)) ? res.user : null;
}
